import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import { suggestionGetQualityFeedbackStatsInternal } from 'Common/api/DazzleInternalService';

interface SuggestionTypeCount {
  suggestion_type: string;
  good_count: number;
  neutral_count: number;
  bad_count: number;
  total: number;
}

interface QualityFeedbackTimePoint {
  date: string;
  good_pct: number;
  neutral_pct: number;
  bad_pct: number;
  total: number;
}

interface QualityFeedbackStats {
  good_count: number;
  neutral_count: number;
  bad_count: number;
  total_rated: number;
  counts_by_type: SuggestionTypeCount[];
  time_series: QualityFeedbackTimePoint[];
}

type DaysBackFilter = 7 | 30 | 90 | null;

const DashboardPage = () => {
  const [stats, setStats] = useState<QualityFeedbackStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [daysBack, setDaysBack] = useState<DaysBackFilter>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      setStatsLoading(true);
      const result = await suggestionGetQualityFeedbackStatsInternal({
        daysBack: daysBack ?? undefined,
      });
      if (isMounted) {
        if (result) {
          setStats(result as QualityFeedbackStats);
        }
        setStatsLoading(false);
      }
    };

    void fetchStats();

    return () => {
      isMounted = false;
    };
  }, [daysBack]);

  const getPercentage = (count: number, total: number): string => {
    if (total === 0) return '0';
    return ((count / total) * 100).toFixed(1);
  };

  const formatSuggestionType = (type: string): string => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderChart = (timeSeries: QualityFeedbackTimePoint[]) => {
    if (timeSeries.length === 0) return null;

    return (
      <ChartContainer>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={timeSeries} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: '#718096' }}
              stroke="#e2e8f0"
            />
            <YAxis
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              tick={{ fontSize: 12, fill: '#718096' }}
              stroke="#e2e8f0"
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                `${value.toFixed(1)}%`,
                name === 'good_pct' ? 'Good' : name === 'neutral_pct' ? 'Neutral' : 'Bad',
              ]}
              labelFormatter={(label: string) => new Date(label).toLocaleDateString()}
              contentStyle={{ fontSize: 13 }}
            />
            <Legend
              formatter={(value: string) =>
                value === 'good_pct' ? 'Good' : value === 'neutral_pct' ? 'Neutral' : 'Bad'
              }
            />
            <Line
              type="monotone"
              dataKey="good_pct"
              stroke="#48BB78"
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="neutral_pct"
              stroke="#ECC94B"
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="bad_pct"
              stroke="#F56565"
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    );
  };

  return (
    <>
      <CompassHeader pageType={PageType.Dashboard} />
      <PageContainer>
        <DashboardCard>
          <CardTitle>Suggestion Quality Feedback</CardTitle>

          <FilterSection>
            <FilterLabel>Time Period:</FilterLabel>
            <FilterButtonGroup>
              <FilterButton $active={daysBack === null} onClick={() => setDaysBack(null)}>
                All Time
              </FilterButton>
              <FilterButton $active={daysBack === 7} onClick={() => setDaysBack(7)}>
                7 Days
              </FilterButton>
              <FilterButton $active={daysBack === 30} onClick={() => setDaysBack(30)}>
                30 Days
              </FilterButton>
              <FilterButton $active={daysBack === 90} onClick={() => setDaysBack(90)}>
                90 Days
              </FilterButton>
            </FilterButtonGroup>
          </FilterSection>

          {statsLoading ? (
            <LoadingText>Loading...</LoadingText>
          ) : stats ? (
            <>
              <StatsContainer>
                <StatCard>
                  <StatValue>{stats.total_rated}</StatValue>
                  <StatLabel>Total Rated</StatLabel>
                </StatCard>
                <StatCard $color="#48BB78">
                  <StatValue>{stats.good_count}</StatValue>
                  <StatLabel>Good ({getPercentage(stats.good_count, stats.total_rated)}%)</StatLabel>
                </StatCard>
                <StatCard $color="#ECC94B">
                  <StatValue>{stats.neutral_count}</StatValue>
                  <StatLabel>Neutral ({getPercentage(stats.neutral_count, stats.total_rated)}%)</StatLabel>
                </StatCard>
                <StatCard $color="#F56565">
                  <StatValue>{stats.bad_count}</StatValue>
                  <StatLabel>Bad ({getPercentage(stats.bad_count, stats.total_rated)}%)</StatLabel>
                </StatCard>
              </StatsContainer>

              {stats.time_series && stats.time_series.length > 0 && (
                <ChartSection>
                  <SectionTitle>Daily Feedback % (7-Day Rolling Average)</SectionTitle>
                  {renderChart(stats.time_series)}
                </ChartSection>
              )}

              {stats.counts_by_type && stats.counts_by_type.length > 0 && (
                <TypeBreakdownSection>
                  <SectionTitle>Feedback by Suggestion Type</SectionTitle>
                  <TypeTable>
                    <TypeTableHeader>
                      <TypeTableHeaderCell>Type</TypeTableHeaderCell>
                      <TypeTableHeaderCell $align="center">Good</TypeTableHeaderCell>
                      <TypeTableHeaderCell $align="center">Neutral</TypeTableHeaderCell>
                      <TypeTableHeaderCell $align="center">Bad</TypeTableHeaderCell>
                      <TypeTableHeaderCell $align="center">Total</TypeTableHeaderCell>
                    </TypeTableHeader>
                    <TypeTableBody>
                      {stats.counts_by_type
                        .sort((a, b) => b.total - a.total)
                        .map((typeCount) => (
                          <TypeTableRow key={typeCount.suggestion_type}>
                            <TypeTableCell>{formatSuggestionType(typeCount.suggestion_type)}</TypeTableCell>
                            <TypeTableCell $align="center">
                              <FeedbackCount $color="#48BB78">{typeCount.good_count}</FeedbackCount>
                            </TypeTableCell>
                            <TypeTableCell $align="center">
                              <FeedbackCount $color="#ECC94B">{typeCount.neutral_count}</FeedbackCount>
                            </TypeTableCell>
                            <TypeTableCell $align="center">
                              <FeedbackCount $color="#F56565">{typeCount.bad_count}</FeedbackCount>
                            </TypeTableCell>
                            <TypeTableCell $align="center">
                              <strong>{typeCount.total}</strong>
                            </TypeTableCell>
                          </TypeTableRow>
                        ))}
                    </TypeTableBody>
                  </TypeTable>
                </TypeBreakdownSection>
              )}
            </>
          ) : (
            <CardDescription>Unable to load stats</CardDescription>
          )}
        </DashboardCard>
      </PageContainer>
    </>
  );
};

const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DashboardCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #E4E7EF;
  transition: box-shadow 0.15s ease;
`;

const CardTitle = styled.h1`
  margin: 0 0 24px 0;
  font-size: 22px;
  font-weight: 700;
  color: #1A1D2B;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 15px;
  color: #8890A5;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 15px;
  color: #8890A5;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #5A6178;
`;

const FilterButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ $active: boolean; $color?: string }>`
  padding: 7px 16px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.$active ? props.$color || '#008AFF' : '#E4E7EF')};
  background: ${(props) => (props.$active ? (props.$color || '#008AFF') + '12' : 'white')};
  color: ${(props) => (props.$active ? props.$color || '#008AFF' : '#5A6178')};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(props) => props.$color || '#008AFF'};
    background: ${(props) => (props.$color || '#008AFF') + '08'};
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: ${(props) => (props.$color ? `${props.$color}0A` : '#F8F9FC')};
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  border-left: 3px solid ${(props) => props.$color || '#008AFF'};
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1A1D2B;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #8890A5;
  font-weight: 500;
`;

const ChartSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #E4E7EF;
`;

const ChartContainer = styled.div`
  height: 320px;
`;

const TypeBreakdownSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #E4E7EF;
`;

const SectionTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #5A6178;
`;

const TypeTable = styled.div`
  border: 1px solid #E4E7EF;
  border-radius: 12px;
  overflow: hidden;
`;

const TypeTableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  background: #F8F9FC;
  border-bottom: 1px solid #E4E7EF;
`;

const TypeTableHeaderCell = styled.div<{ $align?: string }>`
  padding: 10px 16px;
  font-weight: 600;
  font-size: 11px;
  color: #5A6178;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  text-align: ${(props) => props.$align || 'left'};
`;

const TypeTableBody = styled.div``;

const TypeTableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  border-bottom: 1px solid #E4E7EF;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #F8F9FC;
  }
`;

const TypeTableCell = styled.div<{ $align?: string }>`
  padding: 12px 16px;
  font-size: 14px;
  color: #1A1D2B;
  text-align: ${(props) => props.$align || 'left'};
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$align === 'center' ? 'center' : 'flex-start')};
`;

const FeedbackCount = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  padding: 4px 10px;
  border-radius: 4px;
  background: ${(props) => props.$color}20;
  color: ${(props) => props.$color};
  font-size: 13px;
  font-weight: 600;
`;

export default DashboardPage;
